<template>
  <div
    class="slider-widget container"
    :class="{
      'slider-widget--show-value': props.showValue && props.layout !== 'stacked',
      'slider-widget--stacked': props.layout === 'stacked',
      'slider-widget--thin': props.thin,
      'slider-widget--filled': props.filledTrack,
      [`slider-widget--track-${props.trackVariant}`]: props.filledTrack,
    }"
  >
    <template v-if="props.layout === 'stacked'">
      <div class="slider-widget-stacked-meta">
        <label
          v-if="props.showName"
          :for="props.id"
          class="form-label mb-0 fw-normal slider-widget-label slider-widget-label--stacked"
          :style="props.disabled ? 'color: #adb5bd' : ''"
        >
          {{ props.name }}
        </label>
        <span
          v-if="props.showValue && displayReady"
          class="slider-widget-value slider-widget-value--stacked"
          :class="{ 'slider-widget-value-disabled': props.disabled }"
        >
          {{ formattedDisplayValue }}
        </span>
      </div>
      <div class="slider-widget-track-wrap slider-widget-track-wrap--full">
        <div
          v-if="props.filledTrack"
          class="slider-widget-static-track"
          aria-hidden="true"
        ></div>
        <div
          v-if="!props.showValue"
          v-show="visibleTooltip"
          :style="`left: ${tooltipLeft}px`"
          ref="sliderTooltip"
          class="range-slider-tooltip text-center"
        >
          {{ tooltipText }}
        </div>
        <input
          ref="sliderInput"
          type="range"
          :class="`form-range ${rangeType} range-slider`"
          :min="props.min"
          :max="props.max"
          :step="props.step"
          :id="props.id"
          v-model="value"
          @change="onSliderChanged"
          @mousedown="onMouseDown"
          @mouseup="onMouseUp"
          @touchstart="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd"
          v-touch:press="onTouchPress"
          v-touch:release="onTouchRelease"
          :disabled="props.disabled"
        />
      </div>
    </template>
    <div v-else class="slider-widget-row row align-items-center">
      <div
        :class="`col-${props.nameWidth}`"
        v-if="props.showName"
      >
        <label
          :for="props.id"
          class="form-label mb-0 fw-normal slider-widget-label"
          :style="props.disabled ? 'color: #adb5bd' : ''"
        >
          {{ props.name }}
        </label>
      </div>
      <div :class="props.showName ? 'col' : 'col-12'" class="slider-widget-control-col">
        <div class="slider-widget-track-row">
          <div class="slider-widget-track-wrap">
            <div
              v-if="props.filledTrack"
              class="slider-widget-static-track"
              aria-hidden="true"
            ></div>
            <div
              v-if="!props.showValue"
              v-show="visibleTooltip"
              :style="`left: ${tooltipLeft}px`"
              ref="sliderTooltip"
              class="range-slider-tooltip text-center"
            >
              {{ tooltipText }}
            </div>
            <input
              ref="sliderInput"
              type="range"
              :class="`form-range ${rangeType} range-slider`"
              :min="props.min"
              :max="props.max"
              :step="props.step"
              :id="props.id"
              v-model="value"
              @change="onSliderChanged"
              @mousedown="onMouseDown"
              @mouseup="onMouseUp"
              @touchstart="onTouchStart"
              @touchmove="onTouchMove"
              @touchend="onTouchEnd"
              v-touch:press="onTouchPress"
              v-touch:release="onTouchRelease"
              :disabled="props.disabled"
            />
          </div>
          <span
            v-if="props.showValue && displayReady"
            class="slider-widget-value"
            :class="{ 'slider-widget-value-disabled': props.disabled }"
          >
            {{ formattedDisplayValue }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  getCurrentInstance,
  computed,
  watch,
  onMounted,
  nextTick,
} from 'vue';
const SLIDER_DRAG_THRESHOLD_PX = 12;
const { emit } = getCurrentInstance();
const props = defineProps({
  name: {
    type: String,
    required: true,
    default: '',
  },
  id: {
    type: String,
    required: true,
    default: 'slider-widget',
  },
  default: {
    type: Number,
    required: true,
    default: 0,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 10,
  },
  step: {
    type: Number,
    required: false,
    default: 1,
  },
  type: {
    type: String,
    default: 'normal',
  },
  showName: {
    type: Boolean,
    default: true,
  },
  nameWidth: {
    type: Number,
    default: 3,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  textList: {
    type: Array,
    default: []
  },
  isFloat: {
    type: Boolean,
    default: false
  },
  showValue: {
    type: Boolean,
    default: true,
  },
  valueSuffix: {
    type: String,
    default: '',
  },
  displayValueOverride: {
    type: [String, Number],
    default: null,
  },
  layout: {
    type: String,
    default: 'inline',
    validator: (v) => ['inline', 'stacked'].includes(v),
  },
  thin: {
    type: Boolean,
    default: false,
  },
  filledTrack: {
    type: Boolean,
    default: false,
  },
  trackVariant: {
    type: String,
    default: 'bright',
    validator: (v) => ['bright', 'temp', 'neutral', 'boiler', 'cooling'].includes(v),
  },
});

const normalizeSliderNumber = (raw, fallback = NaN) => {
  if (Array.isArray(raw)) {
    if (raw.length === 0) {
      return fallback;
    }
    const n = Number(raw[0]);
    return Number.isNaN(n) ? fallback : n;
  }
  if (raw === undefined || raw === null || raw === '') {
    return fallback;
  }
  const n = Number(raw);
  return Number.isNaN(n) ? fallback : n;
};

const resolveDefaultValue = () => {
  const n = normalizeSliderNumber(props.default, props.min);
  return Number.isNaN(n) ? props.min : n;
};

// v-model="props.value"
var value = ref(resolveDefaultValue());
var tooltipLeft = ref(0);
var visibleTooltip = ref(false);
const displayReady = ref(false);
const sliderInput = ref();
const sliderTooltip = ref();
const lastCommittedValue = ref(resolveDefaultValue());
const defaultValue = computed(() => props.default);
const currentSliderValue = computed(() => normalizeSliderNumber(value.value, props.min));
const tooltipText = computed(() => {
  const v = currentSliderValue.value;
  if (props.textList.length > (v - props.min) / props.step) {
    return props.textList[(v - props.min) / props.step];
  }
  return props.isFloat ? parseFloat(v).toFixed(1) : v;
});

const displayValueText = computed(() => {
  if (props.displayValueOverride !== null && props.displayValueOverride !== undefined && props.displayValueOverride !== '') {
    return props.displayValueOverride;
  }
  return tooltipText.value;
});

const formattedDisplayValue = computed(() => {
  if (props.displayValueOverride !== null && props.displayValueOverride !== undefined && props.displayValueOverride !== '') {
    return String(props.displayValueOverride);
  }
  return `${displayValueText.value}${props.valueSuffix}`;
});

// console.log(`col-${props.nameWidth}`);

const rangeType = computed(() => {
  if (props.type === 'white') {
    return 'range-white';
  }
  return 'range-normal';
});

const syncValueFromDefault = () => {
  const next = resolveDefaultValue();
  value.value = next;
  lastCommittedValue.value = next;
};

const showTooltip = () => {
  if (props.showValue) {
    return;
  }
  visibleTooltip.value = true;
  nextTick(() => (tooltipLeft.value = getTooltipPosition()));
};

const hideTooltip = () => {
  visibleTooltip.value = false;
};

const onMouseDown = () => {
  if (!props.showValue) {
    showTooltip();
  }
};

const onMouseUp = () => {
  if (!props.showValue) {
    hideTooltip();
  }
};

const onTouchPress = () => {
  if (!props.showValue) {
    showTooltip();
  }
};

const onTouchRelease = () => {
  if (!props.showValue) {
    hideTooltip();
  }
};

let touchStartX = 0;
let touchStartY = 0;
let valueAtTouchStart = 0;
let dragActivated = false;
let hasMoved = false;

const onTouchStart = (e) => {
  if (props.disabled || props.showValue) return;
  const t = e.changedTouches?.[0] || e.touches?.[0];
  if (!t) return;
  valueAtTouchStart = lastCommittedValue.value;
  touchStartX = t.clientX;
  touchStartY = t.clientY;
  dragActivated = false;
  hasMoved = false;
};

const onTouchMove = (e) => {
  if (props.disabled || props.showValue) return;
  const t = e.changedTouches?.[0] || e.touches?.[0];
  if (!t) return;
  hasMoved = true;
  if (!dragActivated) {
    const deltaX = t.clientX - touchStartX;
    const deltaY = t.clientY - touchStartY;
    if (Math.abs(deltaX) > SLIDER_DRAG_THRESHOLD_PX && Math.abs(deltaX) > Math.abs(deltaY)) {
      dragActivated = true;
    } else {
      value.value = valueAtTouchStart;
    }
  }
};

const onTouchEnd = () => {
  if (props.showValue) {
    onSliderChanged();
    return;
  }
  if (!dragActivated && hasMoved) {
    value.value = valueAtTouchStart;
  } else {
    onSliderChanged();
  }
};

const onSliderChanged = () => {
  if (props.disabled) {
    return;
  }
  const current = normalizeSliderNumber(value.value, props.min);
  const last = normalizeSliderNumber(lastCommittedValue.value, props.min);
  if (current === last) {
    return;
  }
  value.value = current;
  lastCommittedValue.value = current;
  emit('onSliderChanged', current);
};

const getTooltipPosition = () => {
  let percentage = (100 / (props.max - props.min)) * (value.value - props.min);
  let sliderWidth = sliderInput.value.offsetWidth;
  let tooltipWidth = sliderTooltip.value.offsetWidth;

  let calc = sliderWidth - tooltipWidth + 6;
  let positionCalc =
    (percentage / 100) * calc -
    (6 / ((props.max - props.min) / props.step)) *
      ((props.max - props.min - (value.value - props.min)) / props.step);
  return positionCalc;
};

onMounted(() => {
  visibleTooltip.value = false;
  syncValueFromDefault();
  displayReady.value = true;
  nextTick(() => {
    if (!props.showValue && sliderTooltip.value) {
      tooltipLeft.value = getTooltipPosition();
    }
  });
});

watch(value, (next) => {
  const normalized = normalizeSliderNumber(next, props.min);
  if (Array.isArray(next) || next !== normalized) {
    value.value = normalized;
    return;
  }
  if (!props.showValue && visibleTooltip.value) {
    tooltipLeft.value = getTooltipPosition();
  }
});

watch(defaultValue, () => {
  const next = resolveDefaultValue();
  if (value.value !== next) {
    value.value = next;
    lastCommittedValue.value = next;
  }
});

watch(() => props.showValue, (show) => {
  if (show) {
    visibleTooltip.value = false;
  }
}, { immediate: true });
</script>

<style scoped>
.slider-widget.container {
  padding-left: 0;
  padding-right: 0;
}

.slider-widget-label {
  line-height: 1.2;
}

.slider-widget-control-col {
  padding-top: 0;
  padding-bottom: 0;
}

.slider-widget-track-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.slider-widget-track-wrap {
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: visible;
}

.range-slider-tooltip {
  position: absolute;
  bottom: calc(100% - 4px);
  width: 25px;
  height: 15px;
  z-index: 9;
  background-color: rgba(102, 102, 102, 0.8);
  border: 0 solid #ffffff;
  color: #ffffff;
  border-radius: 3px;
  font-size: 10px;
  line-height: 15px;
  pointer-events: none;
}

.slider-widget-value {
  flex-shrink: 0;
  min-width: 2.75rem;
  text-align: right;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0d6efd;
  line-height: 1;
}

.slider-widget-value-disabled {
  color: #adb5bd;
}

.range-white {
  background-color: transparent;
}

.range-white::-webkit-slider-runnable-track {
  -webkit-appearance: none;
  width: 100%;
  height: 10px;
  background-color: #ffffff;
  border: 1px solid #dddddd;
}

.range-white::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 28px;
  height: 28px;
  background: linear-gradient(90deg, #555555 0%, #666666 100%);
  margin-top: -9px;
}

.range-white::-moz-range-thumb {
  width: 28px;
  height: 28px;
  border: none;
  background: linear-gradient(90deg, #555555 0%, #666666 100%);
}

.range-white:focus::-webkit-slider-thumb {
  width: 28px;
  height: 28px;
  background: linear-gradient(90deg, #666666 0%, #777777 100%);
  margin-top: -9px;
}

.range-white:disabled::-webkit-slider-thumb {
  background: #adb5bd;
}

.range-white:disabled::-moz-range-thumb {
  background: #adb5bd;
}

.range-normal {
  background-color: transparent;
}

.range-normal::-webkit-slider-runnable-track {
  -webkit-appearance: none;
  width: 100%;
  height: 10px;
  border: 1px solid #dddddd;
}

.range-normal::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 28px;
  height: 28px;
  background: linear-gradient(90deg, #555555 0%, #666666 100%);
  margin-top: -9px;
}

.range-normal::-moz-range-thumb {
  width: 28px;
  height: 28px;
  border: none;
  background: linear-gradient(90deg, #555555 0%, #666666 100%);
}

.range-normal:focus::-webkit-slider-thumb {
  width: 28px;
  height: 28px;
  background: linear-gradient(90deg, rgb(102, 102, 102) 0%, #777777 100%);
  margin-top: -9px;
}

.range-normal:disabled::-webkit-slider-thumb {
  background: #adb5bd;
}

.range-normal:disabled::-moz-range-thumb {
  background: #adb5bd;
}

.range-slider-holder {
  position: relative;
  display: flex;
  justify-content: space-between;
  margin: 0 auto 84px;
}

.range-slider-holder p {
  font-size: 2.2rem;
  font-weight: 600;
  margin: 0;
  padding: 18px 18px 18px 0;
}

.range-slider-holder p:last-child {
  padding: 18px 0 18px 18px;
}

.range-slider {
  width: 100%;
  min-height: 44px;
  padding: 8px 0;
  box-sizing: content-box;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  /* 상하 스크롤 시 터치가 슬라이더에 가로채이지 않도록 세로 패닝(스크롤) 허용 */
  touch-action: pan-y;
}

.slider-widget--show-value .range-slider {
  touch-action: manipulation;
}

.slider-widget--stacked {
  padding-left: 0;
  padding-right: 0;
}

.slider-widget-stacked-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.05rem;
}

.slider-widget-label--stacked {
  line-height: 1.25rem;
  font-size: 0.875rem;
}

.slider-widget-value--stacked {
  min-width: auto;
  flex-shrink: 0;
  text-align: right;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0d6efd;
  line-height: 1.25rem;
}

.slider-widget-track-wrap--full {
  width: 100%;
}

.slider-widget-static-track {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 5px;
  border-radius: 999px;
  pointer-events: none;
  z-index: 0;
}

.slider-widget--filled.slider-widget--track-bright .slider-widget-static-track {
  background: linear-gradient(
    to right,
    #ececec 0%,
    #f3e8d8 18%,
    #ffe0b8 45%,
    #ffd080 72%,
    #ffb830 100%
  );
}

.slider-widget--filled.slider-widget--track-temp .slider-widget-static-track {
  background: linear-gradient(
    to right,
    #ffd4a8 0%,
    #fff0dc 28%,
    #f7f7f7 52%,
    #dce8f8 78%,
    #b8cff0 100%
  );
}

.slider-widget--filled.slider-widget--track-neutral .slider-widget-static-track {
  background: linear-gradient(
    to right,
    #e5e5ea 0%,
    #eceff1 45%,
    #dfe3e8 100%
  );
}

.slider-widget--filled:has(.range-slider:disabled) .slider-widget-static-track {
  background: #ececec;
}

.slider-widget--filled .range-slider {
  position: relative;
  z-index: 1;
}

.slider-widget--stacked + .slider-widget--stacked,
.slider-widget--stacked:not(:last-child) {
  margin-bottom: 0;
}

.slider-widget--thin .range-normal::-webkit-slider-runnable-track,
.slider-widget--thin .range-white::-webkit-slider-runnable-track {
  height: 6px;
}

.slider-widget--thin .range-normal::-webkit-slider-thumb,
.slider-widget--thin .range-white::-webkit-slider-thumb {
  width: 20px;
  height: 20px;
  background: #dcdcdc;
  border: 1px solid #c8c8c8;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  margin-top: -7px;
}

.slider-widget--thin .range-normal::-moz-range-track,
.slider-widget--thin .range-white::-moz-range-track {
  height: 6px;
}

.slider-widget--thin .range-normal::-moz-range-thumb,
.slider-widget--thin .range-white::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border: 1px solid #c8c8c8;
  background: #dcdcdc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.slider-widget--thin .range-normal:focus::-webkit-slider-thumb,
.slider-widget--thin .range-white:focus::-webkit-slider-thumb {
  width: 20px;
  height: 20px;
  background: #e8e8e8;
  margin-top: -7px;
}

.slider-widget--thin .range-normal:disabled::-webkit-slider-thumb,
.slider-widget--thin .range-white:disabled::-webkit-slider-thumb,
.slider-widget--thin .range-normal:disabled::-moz-range-thumb,
.slider-widget--thin .range-white:disabled::-moz-range-thumb {
  background: #e9ecef;
  border-color: #dee2e6;
}

.slider-widget--thin .range-slider {
  min-height: 36px;
  padding: 6px 0;
}

.slider-widget--thin.slider-widget--filled .range-normal::-webkit-slider-runnable-track,
.slider-widget--thin.slider-widget--filled .range-white::-webkit-slider-runnable-track {
  height: 5px;
  border: none;
  border-radius: 999px;
  background: transparent;
}

.slider-widget--thin.slider-widget--filled .range-normal::-moz-range-track,
.slider-widget--thin.slider-widget--filled .range-white::-moz-range-track {
  height: 5px;
  border: none;
  border-radius: 999px;
  background: transparent;
}

.slider-widget--thin.slider-widget--filled .range-normal::-moz-range-progress,
.slider-widget--thin.slider-widget--filled .range-white::-moz-range-progress {
  background: transparent;
}

.slider-widget--thin.slider-widget--filled .slider-widget-track-wrap {
  display: flex;
  align-items: center;
  min-height: 22px;
}

.slider-widget--thin.slider-widget--filled .range-normal::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled .range-white::-webkit-slider-thumb {
  width: 22px;
  height: 22px;
  background: #ffffff;
  border: none;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14), 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: -8.5px;
}

.slider-widget--thin.slider-widget--filled .range-normal::-moz-range-thumb,
.slider-widget--thin.slider-widget--filled .range-white::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14), 0 1px 3px rgba(0, 0, 0, 0.1);
}

.slider-widget--thin.slider-widget--filled .range-normal:focus::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled .range-white:focus::-webkit-slider-thumb {
  width: 22px;
  height: 22px;
  background: #ffffff;
  margin-top: -8.5px;
}

.slider-widget--thin.slider-widget--filled .range-normal:disabled::-webkit-slider-runnable-track,
.slider-widget--thin.slider-widget--filled .range-white:disabled::-webkit-slider-runnable-track,
.slider-widget--thin.slider-widget--filled .range-normal:disabled::-moz-range-track,
.slider-widget--thin.slider-widget--filled .range-white:disabled::-moz-range-track {
  background: transparent;
}

.slider-widget--thin.slider-widget--filled .range-normal:disabled::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled .range-white:disabled::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled .range-normal:disabled::-moz-range-thumb,
.slider-widget--thin.slider-widget--filled .range-white:disabled::-moz-range-thumb {
  background: #f5f5f5;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.slider-widget--thin.slider-widget--filled .range-slider {
  min-height: 22px;
  height: 22px;
  padding: 0;
  margin: 0;
  flex: 1;
  width: 100%;
}

/* 커튼 등: 밝기/색온도와 동일 크기, 노브는 한 단계 진한 회색 */
.slider-widget--thin.slider-widget--filled.slider-widget--track-neutral .range-normal::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled.slider-widget--track-neutral .range-white::-webkit-slider-thumb {
  background: #d8d8d8;
  border: 1px solid #bdbdbd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14), 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: -8.5px;
}

.slider-widget--thin.slider-widget--filled.slider-widget--track-neutral .range-normal::-moz-range-thumb,
.slider-widget--thin.slider-widget--filled.slider-widget--track-neutral .range-white::-moz-range-thumb {
  background: #d8d8d8;
  border: 1px solid #bdbdbd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14), 0 1px 3px rgba(0, 0, 0, 0.1);
}

.slider-widget--thin.slider-widget--filled.slider-widget--track-neutral .range-normal:focus::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled.slider-widget--track-neutral .range-white:focus::-webkit-slider-thumb {
  background: #e0e0e0;
  margin-top: -8.5px;
}

.slider-widget--filled.slider-widget--track-boiler .slider-widget-static-track,
.slider-widget--filled.slider-widget--track-cooling .slider-widget-static-track {
  background: linear-gradient(
    to right,
    #e5e5ea 0%,
    #eceff1 45%,
    #dfe3e8 100%
  );
}

.slider-widget--thin.slider-widget--filled.slider-widget--track-boiler .range-normal::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled.slider-widget--track-boiler .range-white::-webkit-slider-thumb {
  background: #f0a8a8;
  border: 1px solid #d96a6a;
  box-shadow: 0 2px 8px rgba(198, 40, 40, 0.2), 0 1px 3px rgba(198, 40, 40, 0.14);
  margin-top: -8.5px;
}

.slider-widget--thin.slider-widget--filled.slider-widget--track-boiler .range-normal::-moz-range-thumb,
.slider-widget--thin.slider-widget--filled.slider-widget--track-boiler .range-white::-moz-range-thumb {
  background: #f0a8a8;
  border: 1px solid #d96a6a;
  box-shadow: 0 2px 8px rgba(198, 40, 40, 0.2), 0 1px 3px rgba(198, 40, 40, 0.14);
}

.slider-widget--thin.slider-widget--filled.slider-widget--track-boiler .range-normal:focus::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled.slider-widget--track-boiler .range-white:focus::-webkit-slider-thumb {
  background: #f8bcbc;
  margin-top: -8.5px;
}

.slider-widget--thin.slider-widget--filled.slider-widget--track-cooling .range-normal::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled.slider-widget--track-cooling .range-white::-webkit-slider-thumb {
  background: #9ecbf5;
  border: 1px solid #4f9de8;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2), 0 1px 3px rgba(33, 150, 243, 0.14);
  margin-top: -8.5px;
}

.slider-widget--thin.slider-widget--filled.slider-widget--track-cooling .range-normal::-moz-range-thumb,
.slider-widget--thin.slider-widget--filled.slider-widget--track-cooling .range-white::-moz-range-thumb {
  background: #9ecbf5;
  border: 1px solid #4f9de8;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2), 0 1px 3px rgba(33, 150, 243, 0.14);
}

.slider-widget--thin.slider-widget--filled.slider-widget--track-cooling .range-normal:focus::-webkit-slider-thumb,
.slider-widget--thin.slider-widget--filled.slider-widget--track-cooling .range-white:focus::-webkit-slider-thumb {
  background: #b3d8f8;
  margin-top: -8.5px;
}

.slider-widget--stacked.slider-widget--thin.slider-widget--filled .slider-widget-track-wrap {
  margin-top: 0;
}
</style>
